import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { KPICards } from '../components/dashboard/KPICards';
import { DonutChart } from '../components/dashboard/DonutChart';
import { BarRank } from '../components/dashboard/BarRank';
import { WorldMap } from '../components/dashboard/WorldMap';
import { IndiaMap } from '../components/dashboard/IndiaMap';
import indiaPorts from '../components/dashboard/india_ports.json';
import { PeriodFilter } from '../components/dashboard/PeriodFilter';
import { fmtT, fmtK, BAR_GRADIENTS, COAST_COLORS } from '../components/dashboard/theme';
import { FloatingFAB } from '../components/dashboard/FloatingFAB';
import { DashboardSkeleton } from '../components/dashboard/DashboardSkeleton';

const API = process.env.REACT_APP_BACKEND_URL || '';

const COAST_ORDER = ['East', 'West', 'Inland'];

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const price = payload.find(p => p.dataKey === 'price');
  const qty = payload.find(p => p.dataKey === 'qty');
  return (
    <div className="bg-dash-panel border border-dash-line rounded-lg px-3 py-2 text-xs font-mono min-w-[150px]">
      <div className="text-dash-ink font-bold mb-1">{label}</div>
      {price && <div className="flex justify-between gap-4"><span style={{ color: '#FF6B52' }}>Avg $/kg</span><span className="text-dash-ink">${price.value?.toFixed(2)}</span></div>}
      {qty && <div className="flex justify-between gap-4"><span className="text-dash-amber">Volume (t)</span><span className="text-dash-ink">{fmtT(qty.value)}</span></div>}
    </div>
  );
};

const GUIDE = {
  sections: [
    { title: 'World Map', desc: 'Each bubble is a destination country, sized by export value. Colour = region. Click any country to drill down.', example: 'Example: United States is the largest bubble ($4.56B) with 947 buyers and 180 exporters.' },
    { title: 'KPI Cards', desc: 'Shipments, Exporters, and Buyers update when you select a country. Many buyers + few exporters = low competition.', example: 'Example: Russia has 107 buyers but only 36 exporters — far less crowded than the US.' },
    { title: 'Customer & Product Donuts', desc: 'Top 9 buyers and product forms for the selected market. A dominant slice = concentrated risk or opportunity.', example: 'Example: In the US, AZ Gems alone accounts for $575M.' },
    { title: 'Exporters & Sizes Bars', desc: 'Ranked Indian shippers and count-grade demand. Compare Sharat\'s position against competitors.', example: 'Example: 31/40 is the top grade globally ($1.55B).' },
  ],
  lookFor: "Countries where Sharat is present but ranks low = room to grow. High value with no Sharat presence = new-entry targets. Products outside Sharat's current mix = cross-sell opportunities.",
};

export default function ExportIntelligenceReact() {
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [origData, setOrigData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState('val');
  const [selected, setSelected] = useState('__ALL__');
  const [fromIdx, setFromIdx] = useState(0);
  const [toIdx, setToIdx] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPort, setSelectedPort] = useState(null);

  // Load main data (embedded JSON from static file)
  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/static/export_intelligence_full.json`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/api/static/intelligence_monthly.json`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([main, mon]) => {
      setData(main);
      setOrigData(JSON.parse(JSON.stringify(main)));
      setMonthly(mon);
      setToIdx(mon.months.length - 1);
      setLoading(false);
    }).catch(e => {
      setError(e.message);
      setLoading(false);
    });
  }, []);

  const drill = useMemo(() => data?.drill?.[selected] || null, [data, selected]);
  const kpi = useMemo(() => drill?.kpi || null, [drill]);
  const countryList = useMemo(() => {
    if (!data?.clist) return [];
    let list = [...data.clist];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(c => c.name.toLowerCase().includes(term));
    }
    return list;
  }, [data, searchTerm]);

  const handleCountryClick = useCallback((name) => {
    setSelected(prev => prev === name ? '__ALL__' : name);
  }, []);

  const applyPeriod = useCallback(() => {
    if (!monthly || !origData) return;
    if (fromIdx > toIdx) { alert('From must be before To'); return; }

    const cm = monthly.country_monthly;
    const dm = monthly.drill_monthly;
    const newData = JSON.parse(JSON.stringify(origData));

    // Rebuild country list
    const newClist = [];
    for (const cname in cm) {
      if (cname === '__ALL__') continue;
      const md = cm[cname];
      let val = 0, qty = 0, ship = 0;
      for (let i = fromIdx; i <= toIdx; i++) { val += md[i][0]; qty += md[i][1]; ship += md[i][2]; }
      if (val <= 0) continue;
      const orig = origData.clist?.find(c => c.name === cname);
      newClist.push({ name: cname, val, qty, ship, buyers: orig?.buyers || 0, price: qty > 0 ? Math.round(val / qty * 100) / 100 : 0, pct: 0 });
    }
    const totalVal = newClist.reduce((a, c) => a + c.val, 0);
    newClist.forEach(c => { c.pct = totalVal > 0 ? Math.round(c.val / totalVal * 10000) / 100 : 0; });
    newClist.sort((a, b) => b.val - a.val);
    newData.clist = newClist;

    // Top 10 for donut
    const top9 = newClist.slice(0, 9);
    let ov = 0, oq = 0, os = 0;
    newClist.slice(9).forEach(c => { ov += c.val; oq += c.qty; os += c.ship; });
    newData.country = top9.map(c => ({ name: c.name, val: c.val, qty: c.qty, ship: c.ship }));
    if (ov > 0) newData.country.push({ name: 'Others', val: ov, qty: oq, ship: os });

    // Rebuild drill data
    for (const dc in dm) {
      if (!newData.drill?.[dc]) continue;
      if (cm[dc]) {
        let cv = 0, cq = 0, cs = 0;
        for (let i = fromIdx; i <= toIdx; i++) { cv += cm[dc][i][0]; cq += cm[dc][i][1]; cs += cm[dc][i][2]; }
        const ok = origData.drill?.[dc]?.kpi || {};
        newData.drill[dc].kpi = { value: cv, tonnes: cq, shipments: cs, exporters: ok.exporters || 0, buyers: ok.buyers || 0, avg_price: cq > 0 ? Math.round(cv / cq * 100) / 100 : 0 };
      }
      ['customers', 'products', 'sizes', 'exporters'].forEach(dim => {
        const dd = dm[dc]?.[dim];
        if (!dd) return;
        const items = [];
        for (const name in dd) {
          const mvals = dd[name];
          let dv = 0, dq = 0;
          for (let i = fromIdx; i <= toIdx; i++) { dv += mvals[i][0]; dq += mvals[i][1]; }
          if (dv > 0) items.push({ name, val: dv, qty: dq, ship: 0 });
        }
        items.sort((a, b) => b.val - a.val);
        const t8 = items.slice(0, 8);
        let rv = 0, rq = 0;
        items.slice(8).forEach(x => { rv += x.val; rq += x.qty; });
        if (rv > 0) t8.push({ name: 'Others', val: rv, qty: rq, ship: 0 });
        newData.drill[dc][dim] = t8;
      });
    }

    newData.month = monthly.months[fromIdx] + ' \u2013 ' + monthly.months[toIdx];
    setData(newData);
    setSelected('__ALL__');
    setIsFiltered(true);
  }, [monthly, origData, fromIdx, toIdx]);

  const resetPeriod = useCallback(() => {
    if (!origData) return;
    setData(JSON.parse(JSON.stringify(origData)));
    setSelected('__ALL__');
    setIsFiltered(false);
  }, [origData]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="h-full bg-dash-bg flex items-center justify-center">
        <div className="text-dash-coral font-mono text-sm">Error: {error}</div>
      </div>
    );
  }

  const selName = selected === '__ALL__' ? 'All markets (India total)' : selected;
  const periodLabel = isFiltered
    ? `${monthly?.months[fromIdx]} to ${monthly?.months[toIdx]} (${toIdx - fromIdx + 1} month${toIdx - fromIdx === 0 ? '' : 's'})`
    : `All ${monthly?.months?.length || 23} months \u00b7 ${monthly?.months?.[0]} to ${monthly?.months?.[monthly.months.length - 1]}`;

  return (
    <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="export-intelligence-react">
      {/* Header */}
      <div className="max-w-[1320px] mx-auto">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-dash-mut mb-1">
          <span>India</span><span className="text-dash-coral">&middot;</span><span>Vannamei Export Intelligence</span>
        </div>
        <h1 className="text-xl font-bold text-dash-ink mb-1">
          Where India's shrimp went — <span className="text-dash-ice font-mono">{data?.month || 'all months'}</span>
        </h1>

        {/* Metric Toggle */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setMetric('val')}
            className={`px-3 py-1 rounded text-xs font-mono font-bold transition ${metric === 'val' ? 'bg-dash-coral text-white' : 'bg-dash-panel text-dash-mut border border-dash-line'}`}
            data-testid="toggle-val"
          >
            By Value $
          </button>
          <button
            onClick={() => setMetric('qty')}
            className={`px-3 py-1 rounded text-xs font-mono font-bold transition ${metric === 'qty' ? 'bg-dash-ice text-dash-bg' : 'bg-dash-panel text-dash-mut border border-dash-line'}`}
            data-testid="toggle-qty"
          >
            By Volume t
          </button>
        </div>

        {/* Selection bar */}
        <div className="flex items-center gap-3 mb-2">
          <span className="w-2 h-2 rounded-full bg-dash-coral" />
          <span className="text-sm text-dash-mut">Viewing: <b className="text-dash-ink">{selName}</b></span>
          {selected !== '__ALL__' && (
            <button onClick={() => setSelected('__ALL__')} className="text-xs text-dash-ice hover:underline cursor-pointer" data-testid="reset-country">
              &larr; Back to all markets
            </button>
          )}
          {selected === '__ALL__' && <span className="text-xs text-dash-mut ml-auto">Tip: search or click any country</span>}
        </div>

        {/* Period Filter */}
        {monthly && (
          <PeriodFilter
            months={monthly.months}
            fromIdx={fromIdx}
            toIdx={toIdx}
            onFromChange={setFromIdx}
            onToChange={setToIdx}
            onApply={applyPeriod}
            onReset={resetPeriod}
            periodLabel={periodLabel}
            isFiltered={isFiltered}
          />
        )}

        {/* KPIs */}
        <div className="mt-4">
          <KPICards kpi={kpi} metric={metric} />
        </div>

        {/* World Map */}
        <div className="mt-4">
          <WorldMap
            countryData={data?.clist || []}
            metric={metric}
            selectedCountry={selected !== '__ALL__' ? selected : null}
            onCountryClick={handleCountryClick}
          />
        </div>

        {/* Country Search */}
        <div className="mt-4">
          <input
            type="text"
            placeholder="Search countries..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full max-w-xs bg-dash-panel border border-dash-line rounded-lg px-3 py-2 text-sm font-grotesk text-dash-ink placeholder:text-dash-mut/50 focus:outline-none focus:border-dash-ice"
            data-testid="country-search"
          />
        </div>

        {/* Country Ranking Table */}
        <div className="mt-3 bg-dash-panel border border-dash-line rounded-xl overflow-hidden">
          <div className="max-h-[300px] overflow-auto">
            <table className="w-full text-xs font-mono">
              <thead className="sticky top-0 bg-dash-panel2">
                <tr className="text-dash-mut text-left">
                  <th className="px-3 py-2 font-semibold">#</th>
                  <th className="px-3 py-2 font-semibold">Country</th>
                  <th className="px-3 py-2 font-semibold text-right">Value</th>
                  <th className="px-3 py-2 font-semibold text-right">Volume</th>
                  <th className="px-3 py-2 font-semibold text-right">Shipments</th>
                  <th className="px-3 py-2 font-semibold text-right">$/kg</th>
                  <th className="px-3 py-2 font-semibold text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {countryList.map((c, i) => (
                  <tr
                    key={c.name}
                    className={`border-t border-dash-line/40 cursor-pointer transition hover:bg-dash-panel2/50 ${selected === c.name ? 'bg-dash-coral/10' : ''}`}
                    onClick={() => handleCountryClick(c.name)}
                  >
                    <td className="px-3 py-1.5 text-dash-mut">{i + 1}</td>
                    <td className="px-3 py-1.5 text-dash-ink font-semibold">{c.name}</td>
                    <td className="px-3 py-1.5 text-right text-dash-ink">{fmtK(c.val)}</td>
                    <td className="px-3 py-1.5 text-right text-dash-mut">{fmtT(c.qty)}</td>
                    <td className="px-3 py-1.5 text-right text-dash-mut">{c.ship?.toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right text-dash-amber">${c.price?.toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right text-dash-ice">{c.pct?.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Customer & Product Donuts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <DonutChart data={drill?.customers} metric={metric} title="Top Buyers" tag="top 9" />
          <DonutChart data={drill?.products} metric={metric} title="Product Forms" tag="all forms" />
        </div>

        {/* Origin Ports — India map + coast breakdown + port list */}
        <div className="bg-dash-panel border border-dash-line rounded-xl p-4 mt-4">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-grotesk font-semibold text-dash-ink">Origin ports — across all regions of India</h3>
            <span className="text-[10px] font-mono uppercase text-dash-mut border border-dash-line rounded px-2 py-0.5">
              {metric === 'val' ? 'VALUE' : 'VOLUME'}
            </span>
          </div>
          <p className="text-xs text-dash-mut mb-3">
            Which Indian port each shipment leaves from. Bubble size = export share &middot; colour = coast. Click a port (or list row) to see its name &amp; details.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
            <IndiaMap metric={metric} selectedPort={selectedPort} onPortClick={(name) => setSelectedPort(p => p === name ? null : name)} />
            <div>
              {/* Coast cards */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {(() => {
                  const grandTotal = COAST_ORDER.reduce((a, k) => a + (indiaPorts.coast[k] ? (metric === 'val' ? indiaPorts.coast[k].val : indiaPorts.coast[k].qty) : 0), 0);
                  return COAST_ORDER.filter(k => indiaPorts.coast[k]).map((k) => {
                    const d = indiaPorts.coast[k];
                    const v = metric === 'val' ? d.val : d.qty;
                    return (
                      <div key={k} className="bg-dash-panel2 border border-dash-line rounded-lg p-2.5" style={{ borderTop: `2px solid ${COAST_COLORS[k]}` }}>
                        <div className="text-[9px] uppercase tracking-wide text-dash-mut">{k} coast</div>
                        <div className="font-mono font-bold text-dash-ink text-sm mt-1">{metric === 'val' ? fmtK(d.val) : fmtT(d.qty)}</div>
                        <div className="font-mono text-[10px] text-dash-mut mt-0.5">{(v / grandTotal * 100).toFixed(1)}% &middot; {d.ports} ports</div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Port list */}
              <div className="flex flex-col gap-1.5 max-h-[280px] overflow-y-auto pr-1">
                {(() => {
                  const ports = indiaPorts.ports;
                  const maxVal = Math.max(...ports.map(p => metric === 'val' ? p.val : p.qty));
                  return ports.slice().sort((a, b) => (metric === 'val' ? b.val - a.val : b.qty - a.qty)).map((p) => {
                    const v = metric === 'val' ? p.val : p.qty;
                    const w = maxVal > 0 ? (v / maxVal * 100) : 0;
                    const sel = selectedPort === p.name;
                    return (
                      <div
                        key={p.name}
                        onClick={() => setSelectedPort(prev => prev === p.name ? null : p.name)}
                        className={`flex items-center gap-2 text-xs p-1.5 rounded-lg cursor-pointer transition ${sel ? 'bg-dash-coral/10 border border-dash-coral/40' : 'hover:bg-dash-panel2 border border-transparent'}`}
                      >
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COAST_COLORS[p.coast] }} />
                        <span className="w-24 truncate text-dash-ink" title={p.name}>{p.name}</span>
                        <div className="flex-1 h-2.5 bg-dash-bg rounded overflow-hidden">
                          <div className="h-full rounded" style={{ width: `${w.toFixed(0)}%`, background: COAST_COLORS[p.coast] }} />
                        </div>
                        <span className="w-16 text-right font-mono text-dash-mut shrink-0">{metric === 'val' ? fmtK(v) : fmtT(v)}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Trend — price & volume */}
        <div className="bg-dash-panel border border-dash-line rounded-xl p-4 mt-4">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-grotesk font-semibold text-dash-ink">Trend — price &amp; volume</h3>
            <span className="text-[10px] font-mono uppercase text-dash-mut border border-dash-line rounded px-2 py-0.5">MONTHLY</span>
          </div>
          <p className="text-xs text-dash-mut mb-3">Avg realisation ($/kg) and total volume (t). Builds as months are added.</p>
          <div className="h-72">
            {data?.trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.trend} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                  <CartesianGrid stroke="var(--dash-line)" strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--dash-mut)' }} stroke="var(--dash-line)" angle={-45} textAnchor="end" height={55} interval={0} />
                  <YAxis yAxisId="price" tick={{ fontSize: 10, fill: 'var(--dash-mut)' }} stroke="var(--dash-line)" tickFormatter={v => '$' + v} label={{ value: '$/kg', angle: -90, position: 'insideLeft', fill: '#FF6B52', fontSize: 11 }} />
                  <YAxis yAxisId="qty" orientation="right" tick={{ fontSize: 10, fill: 'var(--dash-mut)' }} stroke="var(--dash-line)" tickFormatter={v => fmtT(v)} label={{ value: 'tonnes', angle: 90, position: 'insideRight', fill: 'var(--dash-amber)', fontSize: 11 }} />
                  <Tooltip content={<TrendTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: 'var(--dash-mut)' }} />
                  <Area yAxisId="qty" type="monotone" dataKey="qty" name="Volume (t)" stroke="var(--dash-amber)" strokeDasharray="5 3" strokeWidth={2} fill="var(--dash-amber)" fillOpacity={0.12} dot={{ r: 3, fill: 'var(--dash-amber)' }} />
                  <Line yAxisId="price" type="monotone" dataKey="price" name="Avg $/kg" stroke="#FF6B52" strokeWidth={2} dot={{ r: 3, fill: '#FF6B52' }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-dash-mut text-sm">No trend data available.</div>
            )}
          </div>
        </div>

        {/* Exporters & Sizes Bars */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <BarRank data={drill?.exporters} metric={metric} title="Indian Exporters" gradient={BAR_GRADIENTS.coral} />
          <BarRank data={drill?.sizes} metric={metric} title="Count Grades / Sizes" gradient={BAR_GRADIENTS.ice} />
        </div>

        {/* Floating Action Button */}
        <FloatingFAB guideContent={GUIDE} />
      </div>
    </div>
  );
}
